import { createShipmentRequestPayloads } from './carrierServiceUtils';
import type {
  CarrierRateSelectionResponseMap,
  FedexHubId,
  FetchCurrencyConversionParams,
  FetchCurrencyConversionRatesParams,
  RateCalculationTestRecord,
  RateCalculationTestRequest,
  SearchServiceMethodsParams,
  ZoneApplyPreviewRequest,
  ZonePreviewResponse,
} from './types';
import type {
  CarrierCarrierServiceMethod,
  CarrierCurrencyConversionResult,
  CarrierCurrencyRateResponseV1,
  CarrierServiceMethodCreate,
  CarrierServiceMethodInfo,
  ShiptimeCancelRequest,
} from '@shipium-corp/shipium-ts-types';
import PQueue from 'p-queue';
import GatewayAxiosClient from '../GatewayAxiosClient';
import { getToken } from '../../session';
import { format } from 'date-fns';
import type { ZipZoneUploadData } from '../../../pages/AdminPages/ZipZoneMappings/ZipZoneUpload';
import { ZipZoneAction } from '../../../pages/AdminPages/ZipZoneMappings/ZipZoneUpload';
import axios from 'axios';
import getGatewayUri from '../getGatewayUri';

export class CarrierService {
  public async megaMethodTest(request: RateCalculationTestRequest) {
    let labelVoided = false;
    const payload = {
      currencyCode: 'usd',
      generateLabel: true,
      carrierServiceMethodAllowList: request.carrierServiceMethodAllowList,
      labelParameters: {
        currencyCode: 'usd',
        customLabelEntries: request.customLabelEntries || [],
        testMode: !(request.testMode === false),
        includeLabelImagesInResponse: true,
        labelFormats: ['png', 'zpl'],
        manifest: false,
      },

      shipmentParameters: {
        fulfillmentContext: request.fulfillmentContext,
        saturdayDelivery: request.saturdayDelivery,
        desiredDeliveryDate: request.desiredDeliveryDate,
        businessDaysOfTransit: request.businessDaysOfTransit,
        deliverySignatureOption: request.deliverySignatureOption,
        referenceIdentifier: request.referenceIdentifier,
        purchaseOrderIdentifier: request.purchaseOrderIdentifier,
        shipOption: request.shipOption,
        shipmentTags: request.shipmentTags,
        shippedDateTime: request.shipmentEffectiveDate || new Date().toISOString(),
        orderedDateTime: request.shipmentEffectiveDate || new Date().toISOString(),
        ...(request?.customsInfo
          ? {
              customsInfo: {
                reasonForExport: 'sale',
                aesInternalTransactionNumber: 'X20220613366903',
                invoiceDate: format(new Date(), 'yyyy-MM-dd'),
                customsItems: request?.customsInfo?.customsItems ?? [],
                customsDescription: request?.customsInfo?.customsDescription,
                totalCustomsValue: request?.customsInfo?.totalCustomsValue,
                totalCustomsValueCurrency: request?.customsInfo?.totalCustomsValueCurrency,
              },
            }
          : {}),
        destinationAddress: {
          ...request.shipmentDestinationAddress,
          company: request.shipmentDestinationAddress?.companyName || 'N/A',
          name: request.shipmentDestinationAddress?.name || 'N/A',
          street1: request.shipmentDestinationAddress?.address1,
          street2: request.shipmentDestinationAddress?.address2,
          countryCode: request.shipmentDestinationAddress?.country,
        },
        shipFromAddress: {
          ...request.shipmentSourceAddress,
          company: request.shipmentSourceAddress?.companyName || 'N/A',
          name: request.shipmentSourceAddress?.name || 'N/A',
          street1: request.shipmentSourceAddress?.address1,
          street2: request.shipmentSourceAddress?.address2,
          countryCode: request.shipmentSourceAddress?.country,
        },
        orderItemQuantities: [
          {
            productDetails: request.unsupportedProductDetails || [],
            productId: '123456',
            quantity: 1,
            hazmat: request.hazmatParams?.hazmat,
            hazmatInfo: {
              ...(request.hazmatParams || {}),
            },
          },
        ],
        packagingType: {
          packagingMaterial: request.packagingMaterial,
          packagingSizeName: 'test-package',
          packagingWeight: {
            weight: request.packageWeight?.weight,
            weightUnit: request.packageWeight?.weightUnit,
          },
          linearDimensions: {
            ...request.packagelinearDimensions,
          },
          shipOption: 'Standard',
          shipmentTags: ['test'],
        },
        totalWeight: request.packageWeight,
      },
    };
    const startTime = Date.now();
    const megaMethodResponse = await GatewayAxiosClient.post(
      `/shiptime-proxy/api/v1/shipment/carrierselection/label`,
      payload,
      {
        headers: {
          ...(request.megaProcessVersion ? { 'X-MEGA-PROCESS-VERSION-OVERRIDE': request.megaProcessVersion } : ''),
          authorization: `Bearer ${getToken()}`,
        },
      },
    );
    const endTime = Date.now();
    if (request.preventVoid !== true) {
      try {
        const {
          shipiumShipmentId,
          carrierSelection: { carrierSelectionId },
          carrierLabel: { shipiumLabelId },
        } = megaMethodResponse.data;
        const cancelParams: ShiptimeCancelRequest = {
          shipmentId: shipiumShipmentId,
          carrierSelectionId,
          shipiumLabelId,
          cascadeCancel: true,
        };
        await GatewayAxiosClient.post(`/shiptime-proxy/api/v1/deliveryexperience/cancel`, cancelParams);
        labelVoided = true;
      } catch (e) {
        labelVoided = false;
        console.error(e);
      }
    }
    megaMethodResponse.data.responseBody = JSON.stringify(megaMethodResponse.data);
    megaMethodResponse.data.labelVoided = labelVoided;
    megaMethodResponse.data.requestBody = JSON.stringify(payload);
    megaMethodResponse.data.duration = endTime - startTime;
    return megaMethodResponse.data;
  }
  public async bulkCarrierRateSelectionTest(
    records: RateCalculationTestRecord[],
  ): Promise<RateCalculationTestRecord[]> {
    const queue = new PQueue({ concurrency: 10 });

    for (const record of records) {
      queue.add(async () => {
        if (!record.request) {
          record.errorMessage = `Invalid Request`;
          return;
        }
        const result = await this.carrierRateSelectionTest(record.request);
        const resultValue = Object.values(result);
        if (Array.isArray(resultValue) && resultValue[0].carrierSelection) {
          record.response = resultValue[0].carrierSelection;
          record.errorMessage = undefined;
        } else if (Array.isArray(resultValue) && resultValue[0].error) {
          record.response = undefined;
          record.errorMessage = resultValue[0].error.message;
        } else {
          record.response = undefined;
          record.errorMessage = 'Error occured';
        }
      });
    }

    return records;
  }

  public async carrierRateSelectionTest(request: RateCalculationTestRequest): Promise<CarrierRateSelectionResponseMap> {
    const shipmentRequests = createShipmentRequestPayloads(request);
    const responses: CarrierRateSelectionResponseMap = {};
    for (const shipmentRequest of shipmentRequests) {
      try {
        const shipmentResponse = await GatewayAxiosClient.post(
          `/shiptime-proxy/api/v1/deliveryexperience/shipment`,
          shipmentRequest,
          {
            headers: {
              authorization: getToken(),
            },
          },
        );
        const carrierPayload = {
          currencyCode: 'usd',
          carrierServiceMethodAllowList: request.carrierServiceMethodAllowList,
          shipmentShipDate: request.shipmentEffectiveDate || new Date().toISOString(),
          packagingType: {
            packagingMaterial: request.packagingMaterial,
            packagingSizeName: 'test-package',
            linearDimensions: {
              ...request.packagelinearDimensions,
            },
            packagingWeight: {
              weight: request.packageWeight?.weight,
              weightUnit: request.packageWeight?.weightUnit,
            },
          },
        };
        const carrierRateResponse = await GatewayAxiosClient.post(
          `/shiptime-proxy/api/v1/deliveryexperience/shipment/${shipmentResponse.data.shipiumShipmentId}/carrierSelection`,
          carrierPayload,
          {
            headers: {
              authorization: getToken(),
            },
          },
        );
        responses[shipmentRequest.partnerShipmentId] = {
          status: carrierRateResponse.status,
          carrierSelection: carrierRateResponse.data,
          shipment: shipmentResponse.data,
          key: shipmentRequest.partnerShipmentId,
        };
      } catch (e) {
        responses[shipmentRequest.partnerShipmentId] = {
          status: 500,
          error: e,
          key: shipmentRequest.partnerShipmentId,
        };
      }
    }
    return responses;
  }

  public async fetchExpiringRates(partnerId: string, detectionRange: number) {
    const res = await GatewayAxiosClient.get(`/partners/${partnerId}/carrieraccounts/expiringrates`, {
      params: {
        detectionRange,
      },
      headers: {
        authorization: getToken(),
      },
    });
    return res.data;
  }
  public async getFedexHubIds(): Promise<FedexHubId[]> {
    const res = await GatewayAxiosClient.get('/carrier-proxy/api/v1/carriers/fedex/smartpost/hubids', {
      headers: {
        authorization: `Bearer ${getToken()}`,
      },
    });
    return res.data;
  }
  public async previewZipZones(payload: ZipZoneUploadData): Promise<ZonePreviewResponse> {
    const formData = new FormData();
    formData.append('file', new Blob([payload.csvString], { type: 'text/csv' }));

    const httpMethod =
      payload.action === ZipZoneAction.DELETE ? 'delete' : payload.action === ZipZoneAction.ADD ? 'put' : 'post';
    const res = await axios({
      method: httpMethod,
      baseURL: getGatewayUri(),
      url: `/sim-carrier-proxy/api/v2/carriers/${payload.carrier.carrierId}/zones`,
      data: formData,
      withCredentials: true,
      params: {
        zoneKeyType: payload.type.carrierEnum,
      },
      headers: {
        authorization: `Bearer ${getToken()}`,
      },
      responseType: 'json',
    });
    return res.data;
  }
  public async applyZipZonePreview(payload: ZoneApplyPreviewRequest): Promise<ZonePreviewResponse> {
    return await GatewayAxiosClient.post(
      `/sim-carrier-proxy/api/v2/carriers/${payload.carrierId}/zones/originCountry/${payload.fromCountryCode}/preview/${payload.previewId}`,
      {},
      {
        headers: {
          authorization: `Bearer ${getToken()}`,
          'Content-Type': 'application/json',
        },
      },
    );
  }
  public async fetchUsedV2Carriers() {
    const res = await GatewayAxiosClient.get(`/partners/usedV2Carriers`, {
      headers: {
        authorization: getToken(),
      },
    });
    return res.data;
  }
  public async fetchUpsVerifyOptions() {
    const res = await GatewayAxiosClient.get(`/carriers/ups-ready-oauth`, {
      headers: {
        authorization: getToken(),
      },
    });
    return res.data;
  }
  public async saveUpsVerifyAuthCode(authCode: string) {
    const res = await GatewayAxiosClient.post(
      `/carriers/ups-ready-oauth/auth-code`,
      { authCode },
      {
        headers: {
          authorization: getToken(),
        },
      },
    );
    return res.data;
  }
  public async fetchUpsVerifyAuthCode() {
    const res = await GatewayAxiosClient.get(`/carriers/ups-ready-oauth/auth-code`, {
      headers: {
        authorization: getToken(),
      },
    });
    return res.data;
  }
  public async deleteUpsVerifyAuthCodes() {
    const res = await GatewayAxiosClient.delete(`/carriers/ups-ready-oauth/auth-code`, {
      headers: {
        authorization: getToken(),
      },
    });
    return res.data;
  }
  public async fetchServiceMethods(params: SearchServiceMethodsParams): Promise<CarrierServiceMethodInfo[]> {
    const { dataSource = 'yaml', serviceMethodType = 'standard' } = params;
    const res = await GatewayAxiosClient.get('/carrier-proxy/api/v2/carriers/serviceMethods', {
      params: {
        dataSource,
        serviceMethodType,
      },
    });
    return res.data;
  }
  public async fetchServiceMethod(serviceMethodId: string): Promise<CarrierCarrierServiceMethod> {
    const res = await GatewayAxiosClient.get(`carrier-proxy/api/v2/carriers/serviceMethods/${serviceMethodId}`);
    return res.data;
  }
  public async createPartnerServiceMethod(create: CarrierServiceMethodCreate): Promise<CarrierServiceMethodInfo> {
    const res = await GatewayAxiosClient.post(`/carrier-proxy/api/v2/carriers/partnerServiceMethods`, create);
    return res.data;
  }

  //currency rates and conversion
  public async fetchCurrencyConversionRates(
    payload: FetchCurrencyConversionRatesParams,
  ): Promise<CarrierCurrencyRateResponseV1> {
    const res = await GatewayAxiosClient.get(`/carrier-proxy/api/v1/currencies/${payload.baseCurrencyCode}`, {
      params: {
        asOfDate: payload.asOfDate,
        carrierId: payload.carrierId,
      },
    });
    return res.data;
  }

  public async fetchCurrencyConversion(
    payload: FetchCurrencyConversionParams,
  ): Promise<CarrierCurrencyConversionResult> {
    const res = await GatewayAxiosClient.get(`/carrier-proxy/api/v1/currencies/conversion`, {
      params: payload,
    });
    return res.data;
  }
}
